import { store } from "@base/store";
import { LoaderSnackbarWrapperActions } from "@components/organisms/LoaderSnackbarWrapper/LoaderSnackbarWrapper.slice";
import {
  Response,
  httpDelete,
  httpGet,
  httpPatch,
  httpPost,
  httpPut,
} from "@config/http";
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { trackPromise } from "react-promise-tracker";

import { GameProps } from "../game/game.type";
import {
  PaymentBodyProps,
  RsvpBodyProps,
  RsvpMoveSideBodyProps,
  createScoreBodyProps,
} from "./gameDetails.type";

const { REACT_APP_BASE_URL } = process.env;

const initialState: GameProps = {
  title: "",
  description: "",
  accept_cash_payment: false,
  accept_online_payment: false,
  location: "",
  start_date: "",
  end_date: "",
  currency: "",
  fee: 0,
  max_players: 0,
  chat_link: "",
  is_game_admin: false,
  address: {
    type: "",
    geometry: undefined,
    properties: undefined,
  },
  game_admins: [
    {
      id: "",
      user: {
        id: "",
        first_name: "",
        last_name: "",
      },
    },
  ],
  id: "",
  sides: [
    {
      id: "",
      side_name: "",
      players: [
        {
          id: "",
          rsvp: {
            id: "",
            status: "",
            paid: false,
            user: {
              id: "",
              first_name: "",
              last_name: "",
              date_of_birth: "",
            },
          },
        },
      ],
      score: {
        id: "",
        score_value: 0,
      },
    },
  ],
};

const getGameById = createAsyncThunk(
  "game/get game by uuid",
  async ({ id, isAuth }: { id: string; isAuth: boolean }) => {
    let results: Response | null;
    try {
      results = await trackPromise(
        httpGet(`${REACT_APP_BASE_URL}/api/v1/games/${id}`, isAuth)
      );
      return Promise.resolve(results?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const removePlayer = createAsyncThunk(
  "game/remove player form game",
  async ({ gameId, playerId }: { gameId: string; playerId: string }) => {
    let results: Response | null;
    try {
      results = await trackPromise(
        httpDelete(
          `${REACT_APP_BASE_URL}/api/v1/games/${gameId}/players/${playerId}`,
          true
        )
      );
      store.dispatch(
        LoaderSnackbarWrapperActions.setGlobalAppearances({
          alert: {
            isVisible: true,
            message: results?.data?.message,
            type: "success",
          },
        })
      );
      return Promise.resolve(results?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const refundPlayer = createAsyncThunk(
  "refund a player in-app payment",
  async (id: string) => {
    let results: Response | null;
    try {
      results = await trackPromise(
        httpPost(
          `${REACT_APP_BASE_URL}/api/v1/stripe/payment/refund`,
          { rsvp_id: id },
          true
        )
      );
      store.dispatch(
        LoaderSnackbarWrapperActions.setGlobalAppearances({
          alert: {
            isVisible: true,
            message: results?.data?.message,
            type: "success",
          },
        })
      );
      return Promise.resolve(results?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const rsvpToAGame = createAsyncThunk(
  "game/RSVP to a game",
  async ({ id, body }: { id: string; body: RsvpBodyProps }) => {
    let results: Response | null;
    try {
      results = await trackPromise(
        httpPatch(`${REACT_APP_BASE_URL}/api/v1/games/${id}/rsvp`, body, true)
      );
      return Promise.resolve(results?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const moveToSide = createAsyncThunk(
  "game/move player to other side",
  async ({
    gameId,
    PlayerId,
    body,
  }: {
    gameId: string;
    PlayerId: string;
    body: RsvpMoveSideBodyProps;
  }) => {
    let results: Response | null;
    try {
      results = await trackPromise(
        httpPatch(
          `${REACT_APP_BASE_URL}/api/v1/games/${gameId}/players/${PlayerId}`,
          body,
          true
        )
      );
      return Promise.resolve(results?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const adjustPaymentStatus = createAsyncThunk(
  "game/adjust payment for a game per user",
  async ({
    gameId,
    rsvpId,
    body,
  }: {
    gameId: string;
    rsvpId: string;
    body: PaymentBodyProps;
  }) => {
    let results: Response | null;
    try {
      results = await trackPromise(
        httpPatch(
          `${REACT_APP_BASE_URL}/api/v1/games/${gameId}/rsvps/${rsvpId}`,
          body,
          true
        )
      );
      return Promise.resolve(results?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const createScore = createAsyncThunk(
  "score/create score",
  async (body: createScoreBodyProps) => {
    let results: Response | null;
    try {
      results = await trackPromise(
        httpPut(`${REACT_APP_BASE_URL}/api/v1/scores/`, body, true)
      );
      return Promise.resolve(results?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const gameDetailsSlice = createSlice({
  name: "gameDetails",
  initialState: initialState,
  reducers: {
    setSelectedGame: (state, action: PayloadAction<GameProps>) => {
      return { ...state, ...action.payload };
    },
    reset: () => initialState,
  },
  extraReducers: (builder) => {},
});

export const gameDetailsReducer = gameDetailsSlice.reducer;

export const gameDetailsActions = {
  ...gameDetailsSlice.actions,
  getGameById,
  rsvpToAGame,
  adjustPaymentStatus,
  createScore,
  removePlayer,
  refundPlayer,
  moveToSide,
};
